/**
 * Foundation · Review — tabella di revisione delle modifiche con scelta per singola riga
 * e comandi rapidi. Generica: il modulo chiamante fornisce come mostrare valori e intestazioni.
 */
import React, { useState } from "react";
import { Lock, RotateCcw, CheckCheck } from "lucide-react";
import { SuiteButton } from "@/components/suite";
import { REVIEW_DECISIONS, decisionsFor, automaticDecisions, updateAllFilled, countDecisions } from "./reviewModel";

const KIND = { new: ["Nuova", "text-emerald-700 bg-emerald-50"], conflict: ["Già compilata", "text-amber-800 bg-amber-50"], same: ["Invariata", "text-suite-neutral-500 bg-suite-neutral-100"], protected: ["Protetta", "text-suite-error bg-red-50"] };

function DecisionToggle({ row, value, onChange, readOnly, testid }) {
  const options = decisionsFor(row.kind);
  if (!options.length || readOnly) return <span className="text-[11px] text-suite-neutral-500" data-testid={testid}>{row.kind === "protected" ? <><Lock size={11} className="inline" /> mai modificata</> : REVIEW_DECISIONS[value] || "—"}</span>;
  return (
    <div className="inline-flex rounded-md border border-suite-neutral-200 p-0.5" data-testid={testid}>
      {options.map((d) => (
        <button key={d} type="button" onClick={() => onChange(d)} data-testid={`${testid}-${d}`} aria-pressed={value === d}
          className={`rounded px-2 py-0.5 text-[11px] transition-colors ${value === d ? (d === "update" ? "bg-suite-primary text-white" : "bg-suite-neutral-800 text-white") : "text-suite-neutral-600 hover:bg-suite-neutral-100"}`}>
          {REVIEW_DECISIONS[d]}
        </button>
      ))}
    </div>
  );
}

export function ChangeReviewTable({ rows, decisions, onChange, readOnly, renderKey, renderCurrent, renderNext, labels = {}, testid = "change-review" }) {
  const [onlyFilled, setOnlyFilled] = useState(false);
  const counts = countDecisions(rows, decisions);
  const visible = onlyFilled ? rows.filter((r) => r.kind === "conflict" || r.kind === "protected") : rows;
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-xs" data-testid={`${testid}-counts`}>
          <span className="rounded bg-suite-primary-light px-2 py-1 text-suite-primary" data-testid={`${testid}-count-update`}>{counts.update} da aggiornare</span>
          <span className="rounded bg-suite-neutral-100 px-2 py-1" data-testid={`${testid}-count-keep`}>{counts.keep + counts.same} invariate</span>
          {!!counts.skip && <span className="rounded bg-suite-neutral-100 px-2 py-1">{counts.skip} saltate</span>}
          {!!counts.protected && <span className="rounded bg-red-50 px-2 py-1 text-suite-error">{counts.protected} protette</span>}
        </div>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-1.5">
            <label className="mr-2 flex items-center gap-1.5 text-xs text-suite-neutral-600">
              <input type="checkbox" checked={onlyFilled} onChange={(e) => setOnlyFilled(e.target.checked)} data-testid={`${testid}-only-filled`} /> Solo già compilate
            </label>
            <SuiteButton size="sm" variant="secondary" icon={CheckCheck} onClick={() => onChange(updateAllFilled(rows, decisions))} data-testid={`${testid}-update-all`}>{labels.updateAll || "Applica a tutte le giornate già compilate"}</SuiteButton>
            <SuiteButton size="sm" variant="ghost" icon={RotateCcw} onClick={() => onChange(automaticDecisions(rows))} data-testid={`${testid}-reset`}>Ripristina scelta automatica</SuiteButton>
          </div>
        )}
      </div>
      <div className="max-h-[calc(100vh-380px)] min-h-[200px] overflow-auto rounded-lg border border-suite-neutral-200 bg-white">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-suite-neutral-50 text-[10px] uppercase tracking-wider text-suite-neutral-500">
            <tr><th className="px-3 py-2 text-left">{labels.key || "Giorno"}</th><th className="px-3 text-left">{labels.current || "Attuale"}</th><th className="px-3 text-left">{labels.next || "Nuovo"}</th><th className="px-3 text-left">Stato</th><th className="px-3 text-left">Scelta</th></tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.key} className={`border-t border-suite-neutral-100 ${r.kind === "conflict" ? "bg-amber-50/30" : ""}`} data-testid={`${testid}-row-${r.key}`}>
                <td className="px-3 py-2 whitespace-nowrap font-mono">{renderKey(r)}</td>
                <td className="px-3 text-suite-neutral-500">{renderCurrent(r)}</td>
                <td className="px-3">{renderNext(r)}</td>
                <td className="px-3"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${KIND[r.kind][1]}`}>{KIND[r.kind][0]}</span></td>
                <td className="px-3 py-1.5"><DecisionToggle row={r} value={decisions[r.key] || r.default_decision} readOnly={readOnly} onChange={(d) => onChange({ ...decisions, [r.key]: d })} testid={`${testid}-decision-${r.key}`} /></td>
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-suite-neutral-400">Nessuna giornata</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ChangeReviewTable;
